import {
  Agent,
  AgentCreate,
  AgentProfile,
  AgentProfileCreate,
  AgentProfilePatchFactory,
  AgentRegistryAPI,
} from './api';

export class MockAgentRegistryClient implements AgentRegistryAPI {
  private profiles: AgentProfile[] = [
    {
      name: 'demo-profile',
      owner: {
        subject: 'demo-user',
        email: 'demo@chanzuckerberg.com',
      },
      grants: [
        {
          aws: {
            accountId: '111111111111',
            roleArn: 'arn:aws:iam::111111111111:role/readonly',
          },
        },
        {
          anthropic: {},
        },
        {
          tailscale: {},
        },
      ],
      runtimeDefaults: {},
      status: {
        grants: [
          {
            provider: 'anthropic',
            state: 'Ready',
          },
          {
            provider: 'tailscale',
            state: 'Ready',
          },
        ],
      },
    },
    {
      name: 'other-profile',
      owner: {
        subject: 'other-user',
        email: 'other@chanzuckerberg.com',
      },
      grants: [],
      runtimeDefaults: {},
      status: {
        conditions: [
          {
            type: 'Ready',
            status: 'True',
          },
        ],
      },
    },
  ];

  private agents: Agent[] = [
    {
      name: 'demo-agent',
      profileName: 'demo-profile',
      suspended: false,
      timeout: 'indefinite',
      runtime: {},
      purpose: 'Interactive development',
      status: {
        phase: 'Running',
        tailscaleHostname: 'demo-user-demo-agent',
      },
    },
  ];

  async listProfiles(): Promise<AgentProfile[]> {
    return this.profiles;
  }

  async getProfile(name: string): Promise<AgentProfile> {
    const profile = this.profiles.find(candidate => candidate.name === name);
    if (!profile) {
      throw new Error(`profile ${name} not found`);
    }
    return profile;
  }

  async createProfile(profile: AgentProfileCreate): Promise<AgentProfile> {
    const created: AgentProfile = {
      name: profile.name,
      owner: {
        subject: 'demo-user',
        email: 'demo@chanzuckerberg.com',
      },
      grants: profile.grants ?? [],
      runtimeDefaults: profile.runtimeDefaults ?? {},
      claude: profile.claude,
      status: {},
    };
    this.profiles = [...this.profiles, created];
    return created;
  }

  async updateProfile(
    name: string,
    patch: AgentProfilePatchFactory,
  ): Promise<AgentProfile> {
    const profile = await this.getProfile(name);
    const resolvedPatch = typeof patch === 'function' ? patch(profile) : patch;
    const updated: AgentProfile = {
      ...profile,
      grants: resolvedPatch.grants ?? profile.grants,
      runtimeDefaults: resolvedPatch.runtimeDefaults ?? profile.runtimeDefaults,
      claude: resolvedPatch.claude ?? profile.claude,
    };
    this.profiles = this.profiles.map(candidate =>
      candidate.name === name ? updated : candidate,
    );
    return updated;
  }

  async deleteProfile(name: string): Promise<void> {
    this.profiles = this.profiles.filter(profile => profile.name !== name);
    this.agents = this.agents.filter(agent => agent.profileName !== name);
  }

  async listAgents(profileName: string): Promise<Agent[]> {
    return this.agents.filter(agent => agent.profileName === profileName);
  }

  async getAgent(name: string): Promise<Agent> {
    const agent = this.agents.find(candidate => candidate.name === name);
    if (!agent) {
      throw new Error(`agent ${name} not found`);
    }
    return agent;
  }

  async createAgent(profileName: string, agent: AgentCreate): Promise<Agent> {
    const created: Agent = {
      name: agent.name,
      profileName,
      suspended: agent.suspended ?? false,
      timeout: agent.timeout,
      runtime: agent.runtime ?? {},
      purpose: agent.purpose,
      status: {
        phase: agent.suspended ? 'Suspended' : 'Pending',
      },
    };
    this.agents = [...this.agents, created];
    return created;
  }

  async suspendAgent(name: string): Promise<Agent> {
    return this.updateAgent(name, true);
  }

  async resumeAgent(name: string): Promise<Agent> {
    return this.updateAgent(name, false);
  }

  async deleteAgent(name: string): Promise<void> {
    this.agents = this.agents.filter(agent => agent.name !== name);
  }

  private updateAgent(name: string, suspended: boolean): Agent {
    const agent = this.agents.find(candidate => candidate.name === name);
    if (!agent) {
      throw new Error(`agent ${name} not found`);
    }
    const updated: Agent = {
      ...agent,
      suspended,
      status: {
        ...agent.status,
        phase: suspended ? 'Suspended' : 'Running',
      },
    };
    this.agents = this.agents.map(candidate =>
      candidate.name === name ? updated : candidate,
    );
    return updated;
  }
}
